import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import config, { API_BASE_URL } from '@/config';
import { clearAuth, getCurrentUser, getToken } from '@/services/auth';
import defaultSettings from '../config/defaultSettings';
import '@ant-design/v5-patch-for-react-19';
const loginPath = '/user/login';

// Public paths from centralized config
const publicPaths = config.publicPaths;

const isPublicPath = (pathname: string) => {
  return publicPaths.some((p) => pathname.startsWith(p));
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    // Try to get user from localStorage first
    const storedUser = getCurrentUser();
    const token = getToken();

    if (storedUser && token) {
      return {
        ...storedUser,
        name: storedUser.full_name,
        avatar:
          'https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png',
      } as API.CurrentUser;
    }

    return undefined;
  };

  const { location } = history;

  // Skip auth check for public paths
  if (isPublicPath(location.pathname)) {
    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // Check authentication for protected paths
  const currentUser = await fetchUserInfo();
  if (!currentUser) {
    history.push(loginPath);
  }

  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: '', // Disabled watermark
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;

      // Skip auth check for public paths
      if (isPublicPath(location.pathname)) {
        return;
      }

      // Redirect to login if not authenticated
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {/* SettingDrawer removed - no gear icon */}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
// Endpoints that should NOT have Authorization header (from config)
const noAuthEndpoints = config.noAuthEndpoints;

export const request: RequestConfig = {
  baseURL: API_BASE_URL,
  timeout: config.api.timeout,
  requestInterceptors: [
    (url: string, options: any) => {
      // Skip Authorization header for auth endpoints
      const isNoAuthEndpoint = noAuthEndpoints.some((ep) => url?.includes(ep));

      // Add Authorization header if token exists and not a no-auth endpoint
      const token = getToken();
      const headers = { ...options.headers };

      if (token && !isNoAuthEndpoint) {
        headers.Authorization = `Bearer ${token}`;
      }

      return {
        url,
        options: { ...options, headers },
      };
    },
  ],
  responseInterceptors: [
    (response: any) => {
      return response;
    },
  ],
  errorConfig: {
    errorHandler: (error: any, opts: any) => {
      // Skip error handling if skipErrorHandler is set
      if (opts?.skipErrorHandler) {
        throw error;
      }

      const { response } = error;

      // Handle 401 Unauthorized - but don't clear auth for login page
      if (response?.status === 401) {
        const { location } = history;
        // Only redirect if not on public path and not already on login
        if (
          !isPublicPath(location.pathname) &&
          location.pathname !== loginPath
        ) {
          clearAuth();
          history.push(loginPath);
        }
      }

      throw error;
    },
  },
};
